/** Hard limits for the two-canvas PDF preview renderer (Issue #2438). */
export const PDF_RENDER_LIMITS = Object.freeze({
  maxScale: 5,
  maxDimensionPx: 8192,
  maxPixels: 32 * 1024 * 1024,
  bytesPerPixel: 4,
  canvasBufferCount: 2,
});

export interface PdfRenderPlan {
  sourceWidth: number;
  sourceHeight: number;
  requestedScale: number;
  effectiveScale: number;
  outputWidth: number;
  outputHeight: number;
  outputPixels: number;
  estimatedCanvasBytes: number;
  budgetClamped: boolean;
}

/**
 * Produces a canvas allocation plan without trusting PDF page metadata.
 *
 * The preview keeps both an offscreen render target and the visible canvas alive
 * during a swap, so the memory estimate includes both RGBA buffers. PDF.js and
 * browser compositing use additional memory; this budget is deliberately a hard
 * upper bound for only the allocations controlled by PdfRenderService.
 */
export function planPdfRender(sourceWidth: number, sourceHeight: number, requestedScale: number): PdfRenderPlan | null {
  if (
    !Number.isFinite(sourceWidth) ||
    !Number.isFinite(sourceHeight) ||
    !Number.isFinite(requestedScale) ||
    sourceWidth <= 0 ||
    sourceHeight <= 0 ||
    requestedScale <= 0
  ) {
    return null;
  }

  const dimensionScale = Math.min(
    PDF_RENDER_LIMITS.maxDimensionPx / sourceWidth,
    PDF_RENDER_LIMITS.maxDimensionPx / sourceHeight
  );
  const pixelScale = Math.sqrt(PDF_RENDER_LIMITS.maxPixels / (sourceWidth * sourceHeight));
  const budgetScale = Math.min(dimensionScale, pixelScale);
  const effectiveScale = Math.min(requestedScale, PDF_RENDER_LIMITS.maxScale, budgetScale);
  if (!Number.isFinite(effectiveScale) || effectiveScale <= 0) {
    return null;
  }

  const outputWidth = Math.max(1, Math.floor(sourceWidth * effectiveScale));
  const outputHeight = Math.max(1, Math.floor(sourceHeight * effectiveScale));
  const outputPixels = outputWidth * outputHeight;

  return {
    sourceWidth,
    sourceHeight,
    requestedScale,
    effectiveScale,
    outputWidth,
    outputHeight,
    outputPixels,
    estimatedCanvasBytes: outputPixels * PDF_RENDER_LIMITS.bytesPerPixel * PDF_RENDER_LIMITS.canvasBufferCount,
    budgetClamped: effectiveScale < requestedScale,
  };
}
